import React from 'react'
import { useTheme } from '@mui/material/styles'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import useModals from '@/v2/hooks/useModals'
import Button from '@/v2/components/common/Button'
import Typography from '@/v2/components/common/Typography'
import { DAY_IN_US_FORMAT } from '@/v2/constants'
import { currency } from '@/v2/utils/format'
import PaymentPlans from '../PaymentPlans'
import {
  Wrapper,
  Header,
  Summary,
  WhiteBox,
  Amount,
  Breakdown,
  ActionButtons,
  Row,
} from './styles'

const FinanceInfo = ({
  patient,
  financeInfo,
  creditCards,
  bankAccounts,
  paymentMethods,
  paymentPlans,
}) => {
  const theme = useTheme()
  const { goTo } = useModals()

  // `totalPaid` is gross: refunding an invoice never lowers it (the refund life
  // cycle rewrites refunded_amount, not the invoice's final/due amounts), so on
  // its own it reads as money we kept. Lead with the net figure and keep the
  // gross ones in the caption. Refunds come back negative, but abs() keeps the
  // math right whichever sign the API sends.
  const totalPaid = Number(financeInfo?.totalPaid) || 0
  const totalRefunded = Math.abs(Number(financeInfo?.totalRefunded) || 0)
  const netPaid = totalPaid - totalRefunded
  const hasRefunds = totalRefunded > 0

  return (
    <Wrapper theme={theme.palette}>
      {patient.fakeGfPatientId && (
        <Button
          variant="contained"
          color="secondary"
          sx={{
            margin: '0 0 24px auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            columnGap: '5px',
          }}
          onClick={() => {
            window.open(
              `https://smilebar-data.onrender.com/patients/${patient.fakeGfPatientId}/ledger`,
              '_blank',
              'noreferrer'
            )
          }}
        >
          Legacy Ledger on Smilebar Data
          <ExitToAppIcon />
        </Button>
      )}
      <Header>
        <Typography variant="h1" fontSize="26px">
          Finance
        </Typography>
        <Row>
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              goTo(`/v2/patient/${patient.id}/finance/payment-plan/new`, {
                initialValues: { patient: patient },
              })
            }
          >
            + New Payment
          </Button>
        </Row>
      </Header>

      <Summary>
        <WhiteBox theme={theme.palette}>
          <Typography fontSize="16px">Total Paid</Typography>
          <Amount>
            <Typography
              variant="h2"
              fontSize="20px"
              color={theme.palette[netPaid < 0 ? 'red' : 'black'].main}
            >
              {currency(netPaid.toFixed(2))}
            </Typography>
            {hasRefunds && (
              <Breakdown>
                <Typography fontSize="12px" color={theme.palette.gray.dark}>
                  {`${currency(totalPaid.toFixed(2))} paid`}
                </Typography>
                <Typography fontSize="12px" color={theme.palette.gray.dark}>
                  {`${currency(totalRefunded.toFixed(2))} refunded`}
                </Typography>
              </Breakdown>
            )}
          </Amount>
        </WhiteBox>
        <WhiteBox theme={theme.palette}>
          <Typography fontSize="16px">Total Refunded</Typography>
          <Typography
            variant="h2"
            fontSize="20px"
            color={theme.palette[hasRefunds ? 'red' : 'black'].main}
          >
            {currency((totalRefunded * -1).toFixed(2))}
          </Typography>
        </WhiteBox>
        <WhiteBox theme={theme.palette}>
          <Typography fontSize="16px">Total Fees Paid</Typography>
          <Typography variant="h2" fontSize="20px">
            {currency(Number(financeInfo?.totalProcessingFee).toFixed(2))}
          </Typography>
        </WhiteBox>
        <WhiteBox theme={theme.palette}>
          <Typography fontSize="16px">Upcoming Payment</Typography>
          <Typography variant="h2" fontSize="20px">
            {financeInfo?.upcomingPayment !== null
              ? safeFormat(
                  parseISO(financeInfo?.upcomingPayment),
                  DAY_IN_US_FORMAT
                )
              : ' - '}
          </Typography>
        </WhiteBox>
        <WhiteBox theme={theme.palette}>
          <Typography fontSize="16px">
            {`Overdue ${
              financeInfo.overdueDays ? `(${financeInfo.overdueDays} days)` : ''
            }`}
          </Typography>
          <Typography
            variant="h2"
            fontSize="20px"
            color={
              theme.palette[financeInfo.overdueAmount < 0 ? 'red' : 'black']
                .main
            }
          >
            {currency(Number(financeInfo.overdueAmount).toFixed(2))}
          </Typography>
        </WhiteBox>
      </Summary>

      <ActionButtons theme={theme.palette}>
        <Button
          variant="text"
          onClick={() =>
            goTo(`/v2/patient/${patient.id}/finance/manage-accounts/new`, {
              initialValues: { patient },
            })
          }
        >
          Manage Bank Accounts ({bankAccounts.length})
        </Button>

        <Button
          variant="text"
          onClick={() =>
            goTo(`/v2/patient/${patient.id}/finance/manage-cards/new`, {
              initialValues: { patient },
            })
          }
        >
          Manage Credit/Debit Cards ({creditCards.length})
        </Button>

        <Button
          variant="text"
          onClick={() =>
            goTo(`/v2/patient/${patient.id}/finance/receipts`, {
              initialValues: { patient },
            })
          }
        >
          HSA/FSA Receipt
        </Button>
      </ActionButtons>

      <PaymentPlans
        plans={paymentPlans}
        paymentMethods={paymentMethods}
        patientId={patient.id}
      />
    </Wrapper>
  )
}

export default FinanceInfo
